import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuthStore } from '../../stores/authStore';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/helpers';

const ArticlesList = () => {
  const { user } = useAuthStore();
  const isContributor = user?.role === 'contributor';
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await api.get('/articles/all');
      if (response.data.success) {
        console.log('📰 Articles chargés:', response.data.articles);
        setArticles(response.data.articles);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    
    try {
      await api.delete(`/articles/${id}`);
      toast.success('Article supprimé');
      loadArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    
    try {
      await api.put(`/articles/${article.id}`, {
        ...article,
        status: newStatus
      });
      toast.success(newStatus === 'published' ? 'Article publié' : 'Article en brouillon');
      loadArticles();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const filteredArticles = articles.filter(a => 
    filter === 'all' || a.status === filter
  );

  return (
    <AdminLayout>
      <Helmet><title>Articles - Admin Blog</title></Helmet>
      
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Articles</h1>
          <Link to="/admin/articles/new" className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500">
            <i className="fas fa-plus mr-2"></i>Nouvel article
          </Link>
        </div>

        {/* Filtres */}
        <div className="flex gap-3 mb-6">
          {['all', 'published', 'pending', 'rejected', 'draft'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg ${filter === status ? 'bg-yellow-400 text-gray-900' : 'bg-surface text-gray-300'}`}
            >
              {status === 'all' ? 'Tous' 
                : status === 'published' ? 'Publiés' 
                : status === 'pending' ? 'En attente'
                : status === 'rejected' ? 'Rejetés'
                : 'Brouillons'}
            </button>
          ))}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-surface rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface/90 text-gray-400 text-sm">
                <tr>
                  <th className="px-6 py-4 text-left">Titre</th>
                  <th className="px-6 py-4 text-left">Catégorie</th>
                  <th className="px-6 py-4 text-left">Auteur</th>
                  <th className="px-6 py-4 text-left">Statut</th>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                {filteredArticles.map(article => (
                  <tr key={article.id} className="border-t border-gray-700/30 hover:bg-surface/80">
                    <td className="px-6 py-4 font-medium text-white">{article.title}</td>
                    <td className="px-6 py-4">{article.category_name || '-'}</td>
                    <td className="px-6 py-4">{article.author_name}</td>
                    <td className="px-6 py-4">
                      {isContributor && article.status === 'published' ? (
                        // Contributeur : badge statique pour articles publiés
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                          Publié ✓
                        </span>
                      ) : (
                        // Admin ou contributeur (draft/pending/rejected) : bouton interactif
                        <button
                          onClick={() => toggleStatus(article)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            article.status === 'published' 
                              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                              : article.status === 'pending'
                              ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                              : article.status === 'rejected'
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                              : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                          }`}
                          disabled={isContributor && article.status === 'published'}
                        >
                          {article.status === 'published' ? 'Publié' 
                            : article.status === 'pending' ? 'En attente'
                            : article.status === 'rejected' ? 'Rejeté'
                            : 'Brouillon'}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{formatDate(article.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      {console.log(`Article ${article.id}:`, { status: article.status, isContributor, condition: isContributor && article.status === 'published' })}
                      {isContributor && article.status === 'published' ? (
                        // Contributeur : seulement visualiser les articles publiés
                        <Link to={`/article/${article.slug}`} className="text-blue-400 hover:text-blue-300" target="_blank">
                          <i className="fas fa-eye mr-1"></i>
                          Voir
                        </Link>
                      ) : (
                        // Admin ou contributeur (draft/pending/rejected) : éditer et supprimer
                        <>
                          <Link 
                            to={isContributor ? `/contributor/edit/${article.id}` : `/admin/articles/edit/${article.id}`} 
                            className="text-blue-400 hover:text-blue-300 mr-4"
                            title={article.status === 'rejected' ? 'Modifier et resoumettre' : 'Modifier'}
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button onClick={() => handleDelete(article.id)} className="text-red-400 hover:text-red-300">
                            <i className="fas fa-trash"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ArticlesList;
